
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import Datim from '@/components/shared/Datim.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import { identityIntegrationsService, IIdentityIntegrationAuditEntry } from '@/services/identityIntegrationsService';

interface IAuditChange {
  field: string;
  from: string | null;
  to: string;
}

const HIDDEN_FIELDS = ['id', 'uuid', 'created_at', 'updated_at'];
const FINGERPRINT_FIELDS = ['idp_certificate'];
const FINGERPRINT_PREVIEW_LENGTH = 16;

@Component({
  name: 'AuditLogPanel',
  components: { Datim, PaginationBar },
})
export default class AuditLogPanel extends Vue {
  @Prop({ required: true })
  protected integrationId!: number;

  protected loading: boolean = true;
  protected entries: IIdentityIntegrationAuditEntry[] = [];
  protected currentPage: number = 1;
  protected total: number = 0;
  protected perPage: number = 15;
  protected action: string | null = null;
  readonly perPageOptions: number[] = [15, 25, 50];

  created() {
    this.fetchPage(1);
  }

  get lastPage(): number {
    return Math.max(1, Math.ceil(this.total / (this.perPage || 15)));
  }

  get actionOptions() {
    return [
      { text: this.$t('identityIntegrations.audit.actions.insert') as string, value: 'insert' },
      { text: this.$t('identityIntegrations.audit.actions.update') as string, value: 'update' },
      { text: this.$t('identityIntegrations.audit.actions.delete') as string, value: 'delete' },
    ];
  }

  actionLabel(action: string): string {
    const key = 'identityIntegrations.audit.actions.' + action;

    return this.$te(key) ? (this.$t(key) as string) : action;
  }

  actorLabel(entry: IIdentityIntegrationAuditEntry): string {
    if (entry.changed_by_name) {
      return entry.changed_by_name;
    }
    if (entry.user_id) {
      return '#' + entry.user_id;
    }

    return this.$t('identityIntegrations.audit.unknownActor') as string;
  }

  fieldLabel(field: string): string {
    const key = 'identityIntegrations.auditFields.' + field;

    return this.$te(key) ? (this.$t(key) as string) : field;
  }

  changesOf(entry: IIdentityIntegrationAuditEntry): IAuditChange[] {
    const next = entry.new_data || {};
    const previous = entry.old_data || {};
    const changes: IAuditChange[] = [];

    Object.keys(next).forEach((field) => {
      if (HIDDEN_FIELDS.indexOf(field) !== -1) {
        return;
      }

      const from = entry.old_data && field in previous ? this.display(field, previous[field]) : null;
      const to = this.display(field, next[field]);

      if (from === to) {
        return;
      }

      changes.push({ field, from, to });
    });

    return changes;
  }

  private display(field: string, value: any): string {
    if (value === null || value === undefined || value === '') {
      return this.$t('identityIntegrations.audit.none') as string;
    }

    if (FINGERPRINT_FIELDS.indexOf(field) !== -1) {
      return String(value).substring(0, FINGERPRINT_PREVIEW_LENGTH) + '…';
    }

    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : (this.$t('identityIntegrations.audit.none') as string);
    }

    if (typeof value === 'boolean') {
      return value ? (this.$t('common.yes') as string) : (this.$t('common.no') as string);
    }

    if (value === 1 || value === 0) {
      return value === 1 ? (this.$t('common.yes') as string) : (this.$t('common.no') as string);
    }

    return String(value);
  }

  async fetchPage(page: number) {
    this.loading = true;
    try {
      const result = await identityIntegrationsService.auditLog(this.integrationId, page, this.perPage, this.action);
      this.entries = result.data;
      this.currentPage = result.page;
      this.total = result.total;
      this.perPage = result.per_page;
    } finally {
      this.loading = false;
    }
  }

  onPerPageChange(perPage: number) {
    this.perPage = perPage;
    this.fetchPage(1);
  }
}
